import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from "@/components/ui/empty";
import type { CostoDeUnDia } from "@/lib/consumoDeIa";
import { diaCorto, formatearCostoUsd } from "@/lib/formatoDeConsumo";

/**
 * Costo diario del período, en barras.
 *
 * UNA SOLA SERIE Y SIN `domain` EXPLÍCITO, al revés que un gráfico de valores
 * unitarios lejos del cero (ver el gotcha de `webapp/CLAUDE.md`): un costo
 * arranca en cero de verdad, así que el default de recharts (`[0, 'auto']`) es
 * exactamente lo que corresponde acá — nada que calcular.
 *
 * UN DÍA SIN PRECIO CONOCIDO SIGUE DIBUJANDO SU BARRA, en cero: `costo_usd`
 * puede ser `null` para un día entero si ninguna llamada de ese día tenía
 * precio registrado, y esconder la barra sería esconder que ese día existió.
 * El tooltip sí distingue los dos casos — dice "Sin precio" y no "US$ 0,00".
 */
export function GraficoDeCostoDiario({ datos }: { datos: CostoDeUnDia[] }) {
  if (datos.length === 0) {
    return (
      <Empty className="rounded-xl border">
        <EmptyHeader>
          <EmptyTitle>Sin actividad en el período</EmptyTitle>
          <EmptyDescription>
            No se registró ninguna petición a un proveedor de IA en el rango elegido.
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  const puntos = datos.map((d) => ({
    ...d,
    costoNumero: d.costo_usd === null ? 0 : Number(d.costo_usd),
  }));

  return (
    <div className="h-64 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={puntos} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke="rgba(125,129,136,0.18)" />
          <XAxis
            dataKey="fecha"
            tickFormatter={diaCorto}
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 12, fill: "#a6aab0" }}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            width={56}
            tick={{ fontSize: 12, fill: "#a6aab0" }}
            tickFormatter={(valor: number) => formatearCostoUsd(valor.toFixed(6))}
          />
          <Tooltip
            cursor={{ fill: "rgba(76,182,72,0.08)" }}
            contentStyle={{
              background: "var(--popover)",
              border: "1px solid var(--border)",
              borderRadius: 12,
              color: "var(--popover-foreground)",
              fontSize: 13,
            }}
            labelFormatter={diaCorto}
            formatter={(_valor: number, _nombre: string, item: { payload?: CostoDeUnDia }) => [
              formatearCostoUsd(item.payload?.costo_usd ?? null),
              "Costo",
            ]}
          />
          <Bar dataKey="costoNumero" fill="#4cb648" radius={[4, 4, 0, 0]} isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
